import { Button, CreateTableState, Grid, Icon, Typography } from 'gu-ui-kit'
import React, { useEffect, useState } from 'react'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { Link } from 'react-router-dom'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useTranslation } from 'react-i18next'
import StatusIcon
  from 'modules/Referral/PayoutProfiles/List/subcomponents/StatusIcon'
import styles
  from 'modules/Referral/PayoutProfiles/List/subcomponents/List/payout-profiles-list.module.scss'
import {
  StatusBadge,
} from 'modules/Referral/PayoutProfiles/List/subcomponents/StatusBadge'

export const useTableConfig = ({ ...rest }, data = []) => {
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation()
  const breakpoints = useBreakpoint()
  const [columnVisibility, setColumnVisibility] = useState({})
  
  useEffect(() => {
    switch (true) {
      case breakpoints.sm:
        setColumnVisibility({ status: false, balance: false, currency: false })
        break
      case breakpoints.fbs:
        setColumnVisibility({ status: false, currency: false })
        break
      default:
        setColumnVisibility({})
        break
    }
  }, [breakpoints])
  
  const tableConfig = {
    state: {
      columnVisibility,
    },
    columns: [
      {
        id: 'name',
        header: () => t('referral::payout_profiles.table.head.name'),
        cell: ({
          row: {
            original: {
              name,
              status,
              id,
              type,
              card_number,
              wallet,
              is_archived,
              currency,
            },
          },
        }) => (
          <Grid alignItems={'center'} direction={'row'} gap={16} wrap={false}>
            <StatusIcon />
            <Grid direction={'column'} gap={4}>
              <Typography
                noMargin
                color={'text-primary'}
                variant={'Body-Forms-15'}
                component={
                  status !== 'new' ? (
                    <Link
                      to={pathGen(ROUTES.REFERRAL_PAYOUT_PROFILE_INVOICES, {
                        profile_id: id,
                      })}
                      className={styles['row__title']}
                    >
                      {t(`common::payout_types.${type}`)}
                      {': '}
                      {type === 'bank_card' ? card_number : wallet || name}
                    </Link>
                  ) : (
                    <p>
                      {t(`common::payout_types.${type}`)}
                      {': '}
                      {type === 'bank_card' ? card_number : wallet || name}
                    </p>
                  )
                }
              />
              
              <Typography
                variant={'Body-10'}
                component={'div'}
                noMargin
                color={'secondary-grey'}
              >
                {name}
              </Typography>
              {breakpoints.fbs && (
                <Typography color={'Label-Secondary'} variant={'Body-10'}>
                  {t('referral::payout_profiles.table.head.currency')}:{' '}
                  {currency}
                </Typography>)}
              {breakpoints.fbs && (<div>
                  <StatusBadge status={is_archived ? 'declined' : status} />
                </div>
              )}
            </Grid>
          </Grid>
        ),
      },
      {
        id: 'status',
        header: () => t('referral::payout_profiles.table.head.status'),
        cell: ({
          row: {
            original: { is_archived, status },
          },
        }) => <StatusBadge status={is_archived ? 'declined' : status} />,
      },
      {
        id: 'currency',
        header: () => t('referral::payout_profiles.table.head.currency'),
        cell: ({ row }) => (
          <Typography color={'Label-Fifth'} variant={'Body-3'}>
            {row.original.currency}
          </Typography>
        ),
      },
      {
        id: 'invoices',
        header: () => ' ',
        cell: ({
          row: {
            original: { status, id },
          },
        }) => (
          <div>
            {status !== 'new' && (
              <div>
                {status === 'declined' ? (
                  <Button
                    variant={'ghost'}
                    component={Link}
                    to={pathGen(ROUTES.REFERRAL_PAYOUT_PROFILE_EDIT, {
                      profile_id: id,
                    })}
                    title={t('common::button_labels.edit')}
                    color={'Interface-Eighth'}
                    noLabel
                    startIcon={
                      <Icon
                        name={'refresh'}
                        size={'m'}
                        color={'Accent-Error-Primary'}
                      />
                    }
                  />
                ) : (
                  <Button
                    variant={'ghost'}
                    component={Link}
                    to={pathGen(ROUTES.REFERRAL_PAYOUT_PROFILE_INVOICES, {
                      profile_id: id,
                    })}
                    color={'Interface-Eighth'}
                    noLabel
                    title={t('referral::payout_profiles.go_to_invoices')}
                    startIcon={<Icon name={'document'} size={'m'} />}
                  />
                )}
              </div>
            )}
          </div>
        ),
      },
    ],
    showHeader: data?.length > 0,
    ...rest,
  }
  
  return {
    tableState: CreateTableState(tableConfig, data),
    tableConfig,
  }
}
